<template>
    <v-container fluid class="b-container">
        <v-sheet class="pa-3">
            <v-toolbar flat height="90">
                <v-btn
                    :disabled="!getPermission('users.create')"
                    color="secondary"
                    class="text-capitalize px-5"
                    depressed
                    v-text="translations.registerUser[lang]"
                    @click="
                        () => {
                            mode = 'create';
                            dialog = true;
                        }
                    "
                ></v-btn>

                <v-spacer></v-spacer>

                <v-col cols="4" class="pa-0">
                    <b-search-field></b-search-field>
                </v-col>
            </v-toolbar>

            <v-data-table
                :headers="headers"
                :items="users"
                :footer-props="{ itemsPerPageOptions }"
                :loading="loading"
                class="b-outlined"
            >
                <template v-slot:item.created_at="{ item }">
                    {{ formatDate(item.created_at) }}
                </template>

                <template v-slot:item.actions="{ item }">
                    <v-tooltip color="secondary" top>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                :disabled="!getPermission('users.update')"
                                color="yellow darken-3"
                                icon
                                v-on="on"
                                @click="() => {open('update',item)}"
                            >
                                <v-icon
                                    v-text="icons.mdiPencilOutline"
                                ></v-icon>
                            </v-btn>
                        </template>

                        <span
                            class="font-weight-bold"
                            v-text="translations.update[lang]"
                        ></span>
                    </v-tooltip> 
                    <v-tooltip v-if="showUpgrade && !userIsManager(item)" color="secondary" top>
                        <template v-slot:activator="{ on }">
                            <v-btn
                            :disabled="!getPermission('users.update')"
                            color="secondary"
                                icon
                                v-on="on"
                                @click="() => {open('upgrade',item)}"
                            >
                                <v-icon
                                    v-text="icons.mdiChevronDoubleUp"
                                ></v-icon>
                            </v-btn>
                        </template>
                        <span
                            class="font-weight-bold"
                            v-text="translations.upgrade[lang]"
                        ></span>
                    </v-tooltip>
                    <v-tooltip v-if="superUser && userIsManager(item)" color="secondary" top>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                :disabled="!getPermission('users.update')"
                                color="secondary"
                                icon
                                v-on="on"
                                @click="() => {open('downgrade',item)}"
                            >
                                <v-icon
                                    v-text="icons.mdiChevronDoubleDown"
                                ></v-icon>
                            </v-btn>
                        </template>
                        <span
                            class="font-weight-bold"
                            v-text="translations.downgrade[lang]"
                        ></span>
                    </v-tooltip>
                    <v-tooltip v-if="superUser || (loggedManager && !userIsManager(item))" color="secondary" top>
                        <template v-slot:activator="{ on }">
                            <v-btn
                            :disabled="!getPermission('users.delete')"
                            color="red darken-1"
                            icon
                            v-on="on"
                            @click="() => {open('delete',item)}"
                        >
                            <v-icon v-text="icons.mdiClose"></v-icon>
                        </v-btn>
                        </template>
                        <span
                            class="font-weight-bold"
                            v-text="translations.delete[lang]"
                        ></span>
                    </v-tooltip>
                </template>
                
            </v-data-table>

            <v-dialog v-model="dialog" max-width="600">
                <UserForm v-if="['create', 'update'].includes(mode)" :mode="mode" @cancel="dialog = false" />
                <b-card
                    v-else-if="mode"
                :type="mode"
                :title="`${translations[mode][lang]} ${translations.user2[lang]}`"
                :submit-text="translations[mode]"
                :loading="loading"
                :error-message="errorMessage"
                @cancel="closeDialog()"
                @submit="submitDialog()"
            >
                <div class="subtitle-1 font-weight-medium pt-2 px-2">
                    {{ translations.confirmationMessageSure[lang] }}
                    <span v-html="translations[`${mode}ConfirmationMessage`][lang]"></span>
                    <span class="font-weight-bold text--primary">{{
                        user.user
                    }}</span
                    ><span v-if="['upgrade', 'downgrade'].includes(mode)" class="ml-1" v-html="translations[`${mode}To`][lang]"></span>{{ translations.questionmark[lang] }}
                 
                </div>
            </b-card>
            </v-dialog>
        </v-sheet>
    </v-container>
</template>

<script>
import { mdiPencilOutline, mdiClose, mdiChevronDoubleUp, mdiChevronDoubleDown } from "@mdi/js";
import { mapState, mapMutations, mapActions } from "vuex";

import UserForm from "@/components/loyaltyPanel/userRights/UserForm.vue";
import translations from "@/utils/translations/loyaltyPanel/userRights";
import moment from "moment";

export default {
    name: "UserRights",

    components: { UserForm },

    mixins: [translations],

    data: () => ({
        icons: { mdiPencilOutline, mdiClose, mdiChevronDoubleUp, mdiChevronDoubleDown },
        mode: '',
        itemsPerPageOptions: [10, 20, 30, -1]
    }),

    computed: {
        ...mapState(["loading", "errorMessage", "serverItemsLength", "superUser"]),
        ...mapState("loyaltyPanel/userRights", ["users"]),
        lang() {
            return this.$route.params.lang;
        },

        showUpgrade() {
            if (!this.users?.length) {
                return false
            }
            for(let user of this.users){
                if(this.userIsManager(user)){
                    return false
                }
            }
         
            return this.superUser
        },

        loggedManager() {
            if (!this.users?.length) {
                return false
            }
            for(let user of this.users){
                if(user.user_account_id == this.loggedUser?.user_account_id){
                    return this.userIsManager(user)
                }
            }
            return false
        },

        headers() {
            return [
                {
                    text: this.translations.user[this.lang],
                    value: "user"
                },
                {
                    text: this.translations.userRole[this.lang],
                    value: "role"
                },
                { text: this.translations.date[this.lang], value: "created_at" },
                { text: this.translations.actions[this.lang], value: "actions" }
            ];
        },

        dialog: {
            get() {
                return this.$store.state.dialog;
            },

            set(val) {
                this.setDialog(val);
            }
        },

        user: {
            get() {
                return this.$store.state.loyaltyPanel.userRights.user;
            },

            set(val) {
                this.setUser(val);
            },
        },

        loggedUser: {
            get() {
                return this.$store.state.user;
            }
        },

        query() {
            let query = "?";

            for (let key in this.$route.query) {
                query += `${key}=${this.$route.query[key]}&`;
            }

            return query.slice(0, query.length - 1);
        }
    },

    watch: {
        $route(val) {
            if (!val.query.page) {
                this.$router.replace({
                    query: {
                        page: 1,
                        ...this.$route.query,
                    },
                });
            }
            this.getUsers(this.query);
        },

        page(page) {
            this.$router.replace({ query: { ...this.$route.query, page } });
        }
    },

    methods: {
        ...mapMutations(["setDialog", "setDeleteDialog","setResetSuccess","setResetValidation"]),
        ...mapMutations("loyaltyPanel/userRights", ["setUser","setPermissions"]),
        ...mapActions("loyaltyPanel/userRights", ["getUsers","getUser", "create", "update", "upgrade", "downgrade", "delete"]),
        ...mapActions(["getPermission"]),
        open(mode, item){
            this.user = item
            this.mode = mode
            this.dialog = true

        },

        userIsManager(user){
            return user.role.match(/manager/i)?.length > 0
        },

        formatDate(date){
           return moment(date).format("DD/MM/YYYY HH:mm")
        },

        closeDialog(){
            this.user = {}
            this.mode = ''
            this.dialog = false
        },
        
        async submitDialog(){
            if (typeof this[this.mode]  == 'function') {
                await this[this.mode](this.user)
                await this.getUsers(this.query)
            }
        },
    },

    mounted(){
        this.getUsers(this.query)
    },

    beforeCreate() {
        if (!this.$route.query.page) {
            this.$router.replace({
                query: {
                    page: 1,
                    ...this.$route.query,
                },
            });
        }
    }
};
</script>
